import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { registryLegacyRedirectTarget } from "@/lib/registryLegacyRedirect";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CollectorAuthProvider } from "@/context/CollectorAuthContext";
import MarketingConsentPrompt from "@/components/MarketingConsentPrompt";
import Layout from "@/components/Layout";
import ProtectedRoute from "@/components/ProtectedRoute";
import CollectorProtectedRoute from "@/components/CollectorProtectedRoute";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { trackPixelPageView } from "@/lib/metaPixel";
import { readAttributionFromLocation, persistAttribution } from "@/lib/attributionCapture";
import AnalyticsConsentBanner from "@/components/AnalyticsConsentBanner";

import Home from "@/pages/Home";
import BlackOneOfOne from "@/pages/BlackOneOfOne";
import BlackOneOfOneDetail from "@/pages/BlackOneOfOneDetail";
import AdminBlackParallels from "@/pages/admin/AdminBlackParallels";
import AdminBlackParallelReports from "@/pages/admin/AdminBlackParallelReports";
import ReportSightingForm from "@/pages/ReportSightingForm";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Shop from "@/pages/Shop";
import TeamSetsCatalog from "@/pages/TeamSetsCatalog";
import GoldSinglesCatalog from "@/pages/GoldSinglesCatalog";
import MissingStickers from "@/pages/MissingStickers";
// Phase 4 · MissingStickersRussia2018 is retired — `/order-missing/russia-2018`
// now redirects to `/order-missing?collection=russia_2018` via `<Navigate>`.
import ResumeQuote from "@/pages/ResumeQuote";
import QuoteMagicResume from "@/pages/QuoteMagicResume";
import StopQuoteReminders from "@/pages/StopQuoteReminders";
import Events from "@/pages/Events";
import EventDetail from "@/pages/EventDetail";
import CertificateDetail from "@/pages/CertificateDetail";
import Registry from "@/pages/Registry";
import CertificateOrderPage from "@/pages/certificates/CertificateOrder";
import CertificateOrderStatus from "@/pages/certificates/CertificateOrderStatus";
import CollectorProfile from "@/pages/CollectorProfile";
import CollectorsRegistry from "@/pages/CollectorsRegistry";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import PrivacyAnalytics from "@/pages/Privacy";
import TermsOfService from "@/pages/legal/TermsOfService";
import ShippingPolicy from "@/pages/legal/ShippingPolicy";
import ReturnPolicy from "@/pages/legal/ReturnPolicy";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCatalog from "@/pages/admin/AdminCatalog";
import AdminSalesTax from "@/pages/admin/AdminSalesTax";
import AdminSalesDashboard from "@/pages/admin/AdminSalesDashboard";
import AdminCertificateOrders from "@/pages/admin/AdminCertificateOrders";
import AdminCertificateOrderDetail from "@/pages/admin/AdminCertificateOrderDetail";
import AdminAllOrders from "@/pages/admin/AdminAllOrders";
import AdminNewSale from "@/pages/admin/AdminNewSale";
import AdminQuickSale from "@/pages/admin/AdminQuickSale";
import AdminHistoricalEntry from "@/pages/admin/AdminHistoricalEntry";
import AdminSalesOrders from "@/pages/admin/AdminSalesOrders";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminWantedItems from "@/pages/admin/AdminWantedItems";
import AdminPurchases from "@/pages/admin/AdminPurchases";
import AdminExpenses from "@/pages/admin/AdminExpenses";
import AdminInventoryConversions from "@/pages/admin/AdminInventoryConversions";
import AdminCollectorAnalytics from "@/pages/admin/AdminCollectorAnalytics";
import AdminPricing from "@/pages/admin/AdminPricing";
import CollectorRegister from "@/pages/collector/Register";
import CollectorLogin from "@/pages/collector/Login";
import CollectorVerifyEmail from "@/pages/collector/VerifyEmail";
import CollectorForgotPassword from "@/pages/collector/ForgotPassword";
import CollectorResetPassword from "@/pages/collector/ResetPassword";
import CollectorDashboard from "@/pages/collector/Dashboard";
import DashboardLayout from "@/pages/collector/DashboardLayout";
import DashboardHome from "@/pages/collector/DashboardHome";
import MyProfile from "@/pages/collector/MyProfile";
import MyOrders from "@/pages/collector/MyOrders";
import OrderDetail from "@/pages/collector/OrderDetail";
import AccountSettings from "@/pages/collector/AccountSettings";
import ComingSoon from "@/pages/collector/ComingSoon";
// import MyAlbums from "@/pages/collector/MyAlbums";  // Removed — legacy Album Registry redirects to My Collection
import MyCollection from "@/pages/collector/MyCollection";
import AlbumInventory from "@/pages/collector/AlbumInventory";
import PublicAlbum from "@/pages/PublicAlbum";
import CollectorProfileSettings from "@/pages/collector/CollectorProfileSettings";
import CollectorPublicProfile from "@/pages/collector/CollectorPublicProfile";
import MyCertificates from "@/pages/collector/MyCertificates";
import MatchingTab from "@/pages/collector/MatchingTab";
import SmartMatches from "@/pages/collector/SmartMatches";
import CertificateDetailPage from "@/pages/collector/CertificateDetailPage";
import CertificateVerify from "@/pages/CertificateVerify";
import { Layers, MapPin } from "lucide-react";
import CollectorClaim from "@/pages/collector/Claim";
import CollectorClaimSuccess from "@/pages/collector/ClaimSuccess";
import CollectorLinkConfirm from "@/pages/collector/LinkConfirm";
import OnboardingLayout from "@/pages/onboarding/Layout";
import OnboardingProfile from "@/pages/onboarding/Profile";
import OnboardingAlbums from "@/pages/onboarding/AlbumsCompact";
import OnboardingCongrats from "@/pages/onboarding/Congrats";
import OrderTracker from "@/pages/OrderTracker";
import CheckoutAuth from "@/pages/CheckoutAuth";

// Preserve legacy /certificate/{n} links (existing QR codes, prior emails,
// external shares) by permanent client-side redirect to the plural canonical.
function CertificateNumberRedirect() {
  const { cert_number } = useParams();
  return <Navigate to={`/certificates/${cert_number}`} replace />;
}

// Legacy /registry landing page — permanently redirected to /certificates.
// A fixed `<Navigate to="/certificates" replace />` would DROP any query
// string or hash on the incoming URL (React Router does not forward them
// automatically when the destination is a hard-coded string). We forward
// them explicitly via `registryLegacyRedirectTarget` (unit-tested in
// src/lib/registryLegacyRedirect.js) so links like
//     /registry?album=wc2026&year=2026#results
// resolve to
//     /certificates?album=wc2026&year=2026#results
function RegistryLegacyRedirect() {
  const location = useLocation();
  return <Navigate to={registryLegacyRedirectTarget(location)} replace />;
}

// /admin/quotes → /admin?tab=quotes. Preserves any additional query
// filters the Home Dashboard tile may append (e.g. ?range=today).
function AdminQuotesRedirect() {
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  // Force tab=quotes; keep every OTHER incoming param (e.g. range).
  incoming.set("tab", "quotes");
  const search = "?" + incoming.toString();
  return (
    <Navigate
      to={{ pathname: "/admin", search, hash: location.hash }}
      replace
    />
  );
}

// /admin/quotes/:public_id → /admin?tab=quotes&open=<public_id>. Uses
// useParams so the URL stays canonical and QuotesAdmin can restore the
// detail view via ?open=. Fixes the previous 404 on Original Quote
// links from Fulfillment Admin.
function AdminQuoteDetailRedirect() {
  const params = useParams();
  const location = useLocation();
  const incoming = new URLSearchParams(location.search);
  incoming.set("tab", "quotes");
  incoming.set("open", params.public_id || "");
  const search = "?" + incoming.toString();
  return (
    <Navigate
      to={{ pathname: "/admin", search, hash: location.hash }}
      replace
    />
  );
}

// Fires a `page_view` for every route change so GA4 and Clarity see
// SPA navigations. Mounted once inside <BrowserRouter>.
function AnalyticsRouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    // Defer to next tick so <SEO> can update document.title BEFORE
    // we snapshot it into the page_view event.
    const id = setTimeout(() => {
      trackPageView(location.pathname + location.search);
      // Meta Pixel PageView — SPA route change. Skipped inside the
      // module for /admin and deduped per pathname.
      trackPixelPageView(location.pathname + location.search);
    }, 0);
    // Slice 2c: passive attribution capture — never blocks navigation.
    try {
      const payload = readAttributionFromLocation();
      if (payload) persistAttribution(payload);
    } catch (_) { /* attribution failure is silent by design */ }
    return () => clearTimeout(id);
  }, [location.pathname, location.search]);
  return null;
}

// Injects the Google Search Console verification meta tag when the
// env var is set. Preferred verification method is DNS — this is a
// spec §6 fallback for the meta-tag path. The tag is idempotent:
// remounting the app does not create duplicates.
function SearchConsoleMeta() {
  React.useEffect(() => {
    const token = (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION || "").trim();
    if (!token) return;
    const name = "google-site-verification";
    if (document.head.querySelector(`meta[name="${name}"]`)) return;
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    m.setAttribute("content", token);
    document.head.appendChild(m);
  }, []);
  return null;
}

export default function App() {
  React.useEffect(() => { initAnalytics(); }, []);
  return (
    <AuthProvider>
      <CollectorAuthProvider>
        <BrowserRouter>
        <SearchConsoleMeta />
        <AnalyticsRouteTracker />
        <AnalyticsConsentBanner />
        {/* E8C · Global one-time in-app consent capture for
            authenticated collectors whose preference is still null. */}
        <MarketingConsentPrompt />
        <Toaster
          theme="light"
          position="top-right"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              border: "2px solid #0A0A0A",
              borderRadius: "16px",
              color: "#0A0A0A",
              fontFamily: "Nunito, system-ui, sans-serif",
              fontWeight: 600,
              boxShadow: "4px 4px 0 #0A0A0A",
            },
          }}
        />
        <Routes>
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/black-1of1" element={<BlackOneOfOne />} />
            <Route path="/black-1of1/report" element={<ReportSightingForm />} />
            <Route path="/black-1of1/:sticker_number" element={<BlackOneOfOneDetail />} />
            <Route path="/admin/black-1of1" element={<AdminBlackParallels />} />
            <Route path="/admin/black-1of1/reports" element={<AdminBlackParallelReports />} />
            <Route path="/shop" element={<Shop />} />
            <Route path="/shop/team-sets/:variation" element={<TeamSetsCatalog />} />
            <Route path="/shop/gold-singles" element={<GoldSinglesCatalog />} />
            <Route path="/order-missing" element={<MissingStickers />} />
            <Route path="/order-missing/russia-2018" element={<Navigate to="/order-missing?collection=russia_2018" replace />} />
            <Route path="/order-missing/resume" element={<ResumeQuote />} />
            <Route path="/order-missing/quote/:public_id" element={<QuoteMagicResume />} />
            <Route path="/order-missing/quote/:public_id/stop-reminders" element={<StopQuoteReminders />} />
            <Route path="/checkout-auth" element={<CheckoutAuth />} />
            <Route path="/order-missing/submit" element={<Navigate to="/order-missing" replace />} />
            <Route path="/orders/:public_id" element={<OrderTracker />} />
            <Route path="/events" element={<Events />} />
            <Route path="/events/:id" element={<EventDetail />} />
            <Route path="/registry" element={<RegistryLegacyRedirect />} />
            {/* Certificate Purchasing & Album-Verification — dedicated
                order flow (must be declared BEFORE the wildcard
                /certificates/:cert_number route). */}
            <Route path="/certificates/order" element={<CertificateOrderPage />} />
            <Route path="/certificates/order/:public_order_number" element={<CertificateOrderStatus />} />
            <Route path="/certificates" element={<Registry />} />
            <Route path="/certificates/:cert_number" element={<CertificateDetail />} />
            <Route path="/collectors" element={<CollectorsRegistry />} />
            <Route path="/collectors/search" element={<CollectorsRegistry />} />
            <Route path="/collectors/:slug" element={<CollectorPublicProfile />} />
            <Route path="/certificates/verify" element={<CertificateVerify />} />
            <Route path="/certificates/verify/:cert_number" element={<CertificateVerify />} />
            <Route path="/c/:username" element={<CollectorProfile />} />
            <Route path="/c/:username/albums/:album_slug" element={<PublicAlbum />} />
            {/* Legacy singular routes → permanent client-side redirect to plural canonical. */}
            <Route path="/certificate/:cert_number" element={<CertificateNumberRedirect />} />
            <Route path="/certificate" element={<Navigate to="/certificates" replace />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/privacy" element={<PrivacyAnalytics />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/shipping-policy" element={<ShippingPolicy />} />
            <Route path="/return-policy" element={<ReturnPolicy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            {/* Admin Quotes — canonical route. AdminDashboard reads the
                selected tab from `?tab=quotes`, so we forward /admin/quotes
                to that flag via AdminQuotesRedirect (preserves any incoming
                filter query params). */}
            <Route
              path="/admin/quotes"
              element={
                <ProtectedRoute>
                  <AdminQuotesRedirect />
                </ProtectedRoute>
              }
            />
            {/* Original Quote deep-links from Fulfillment Admin, order
                emails, and CRM copy-paste. Kept as its own route so
                the URL survives page reloads and preserves history. */}
            <Route
              path="/admin/quotes/:public_id"
              element={
                <ProtectedRoute>
                  <AdminQuoteDetailRedirect />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/fulfillment/:public_id"
              element={
                <ProtectedRoute>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/catalog"
              element={
                <ProtectedRoute>
                  <AdminCatalog />
                </ProtectedRoute>
              }
            />
            {/* Phase 1a · Sales & Tax reporting foundation (admin-only) */}
            <Route
              path="/admin/sales"
              element={
                <ProtectedRoute>
                  <AdminSalesTax />
                </ProtectedRoute>
              }
            />
            {/* Phase 6A · Unified Sales Dashboard (business-channel reporting) */}
            <Route
              path="/admin/sales-dashboard"
              element={
                <ProtectedRoute>
                  <AdminSalesDashboard />
                </ProtectedRoute>
              }
            />
            {/* Certificate Purchasing · admin dashboard tab */}
            <Route
              path="/admin/certificate-orders"
              element={
                <ProtectedRoute>
                  <AdminCertificateOrders />
                </ProtectedRoute>
              }
            />
            {/* P3.C · Full-page Certificate Order workflow (primary
                Admin surface — replaces the small drawer). */}
            <Route
              path="/admin/certificate-orders/:publicOrderNumber"
              element={
                <ProtectedRoute>
                  <AdminCertificateOrderDetail />
                </ProtectedRoute>
              }
            />
            {/* Admin Operations · All Orders hub (Phase A) */}
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute>
                  <AdminAllOrders />
                </ProtectedRoute>
              }
            />
            {/* Phase 2 · Internal Sales Hub — New Sale workspace */}
            <Route
              path="/admin/new-sale"
              element={
                <ProtectedRoute>
                  <AdminNewSale />
                </ProtectedRoute>
              }
            />
            {/* Phase 2B · Mobile Quick Sale — POS-style rapid checkout */}
            <Route
              path="/admin/quick-sale"
              element={
                <ProtectedRoute>
                  <AdminQuickSale />
                </ProtectedRoute>
              }
            />
            {/* Phase 9A · Historical Entry — dedicated reconstruction workspace */}
            <Route
              path="/admin/historical-entry"
              element={
                <ProtectedRoute>
                  <AdminHistoricalEntry />
                </ProtectedRoute>
              }
            />
            {/* Phase 3 · Sales Orders — deposits, partial payments, balances */}
            <Route
              path="/admin/sales-orders"
              element={
                <ProtectedRoute>
                  <AdminSalesOrders />
                </ProtectedRoute>
              }
            />
            {/* Phase 4A · Customer CRM */}
            <Route
              path="/admin/customers"
              element={
                <ProtectedRoute>
                  <AdminCustomers />
                </ProtectedRoute>
              }
            />
            {/* Phase 5 · Wanted Items */}
            <Route
              path="/admin/wanted-items"
              element={
                <ProtectedRoute>
                  <AdminWantedItems />
                </ProtectedRoute>
              }
            />
            {/* Phase 7A · Purchases (cost capture) */}
            <Route
              path="/admin/purchases"
              element={
                <ProtectedRoute>
                  <AdminPurchases />
                </ProtectedRoute>
              }
            />
            {/* Phase 7A · Expenses (cost capture) */}
            <Route
              path="/admin/expenses"
              element={
                <ProtectedRoute>
                  <AdminExpenses />
                </ProtectedRoute>
              }
            />
            {/* Phase 7A.3 · Explicit Inventory Conversions */}
            <Route
              path="/admin/inventory-conversions"
              element={
                <ProtectedRoute>
                  <AdminInventoryConversions />
                </ProtectedRoute>
              }
            />
            {/* Phase C4 · Collector Analytics dashboard (admin-only, read-only) */}
            <Route
              path="/admin/collector-analytics"
              element={
                <ProtectedRoute>
                  <AdminCollectorAnalytics />
                </ProtectedRoute>
              }
            />
            {/* Phase P2B · Admin Pricing Manager (list, edit master prices, view history) */}
            <Route
              path="/admin/pricing"
              element={
                <ProtectedRoute>
                  <AdminPricing />
                </ProtectedRoute>
              }
            />
            {/* Collector auth (Phase 2) — separate cookies / tokens / context */}
            <Route path="/register" element={<CollectorRegister />} />
            <Route path="/login" element={<CollectorLogin />} />
            <Route path="/verify-email" element={<CollectorVerifyEmail />} />
            <Route path="/forgot-password" element={<CollectorForgotPassword />} />
            <Route path="/reset-password" element={<CollectorResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <CollectorProtectedRoute>
                  <DashboardLayout />
                </CollectorProtectedRoute>
              }
            >
              {/* Sprint · Profile-first — /dashboard now lands on My Profile.
                  The old collection-focused DashboardHome is kept behind an
                  explicit `/dashboard/home` route in case a deep link
                  still points to it. */}
              <Route index element={<MyProfile />} />
              <Route path="home" element={<DashboardHome />} />
              <Route path="orders" element={<MyOrders />} />
              <Route path="orders/:public_id" element={<OrderDetail />} />
              <Route path="account-settings" element={<AccountSettings />} />
              <Route
                path="collector-profile"
                element={<CollectorProfileSettings />}
              />
              <Route
                path="albums"
                element={<Navigate to="/dashboard/my-collection" replace />}
              />
              <Route
                path="albums/*"
                element={<Navigate to="/dashboard/my-collection" replace />}
              />
              <Route
                path="my-collection"
                element={<MyCollection />}
              />
              <Route
                path="my-collection/albums/:album_id"
                element={<AlbumInventory />}
              />
              <Route
                path="certificates"
                element={<MyCertificates />}
              />
              <Route
                path="certificates/:cert_number"
                element={<CertificateDetailPage />}
              />
              {/* Iter V2 Phase B — claim under dashboard so it's protected
                  by CollectorProtectedRoute and header nav stays consistent. */}
              <Route
                path="claim"
                element={<CollectorClaim />}
              />
              <Route
                path="claim/success"
                element={<CollectorClaimSuccess />}
              />
              <Route
                path="collection-tracker"
                element={
                  <ComingSoon
                    testid="coming-soon-tracker"
                    title="Collection Tracker"
                    description="Log the stickers you have, the ones you still need, and the trades you're offering — all synced across your CollectorUp devices."
                    Icon={Layers}
                  />
                }
              />
              <Route
                path="matching"
                element={<MatchingTab />}
              />
              <Route
                path="smart-matches"
                element={<SmartMatches />}
              />
              <Route
                path="addresses"
                element={
                  <ComingSoon
                    testid="coming-soon-addresses"
                    title="Saved Addresses"
                    description="Save your shipping and pickup addresses so future orders check out in a single tap."
                    Icon={MapPin}
                  />
                }
              />
            </Route>
            {/* Legacy tab links (?tab=…) route to the new sections. */}
            <Route path="/dashboard-legacy" element={<CollectorProtectedRoute><CollectorDashboard /></CollectorProtectedRoute>} />

            {/* Universal Collector Onboarding — Phase A + Signup Redesign.
                Reduced from 6 steps to 3 (Profile → Albums → Congrats).
                Legacy step routes (welcome, completion, help) redirect
                into the new flow so pre-redesign links keep working. */}
            <Route path="/onboarding" element={<OnboardingLayout />}>
              <Route index element={<Navigate to="profile" replace />} />
              <Route path="welcome" element={<Navigate to="/onboarding/profile" replace />} />
              <Route path="profile" element={<OnboardingProfile />} />
              <Route path="albums" element={<OnboardingAlbums />} />
              <Route path="completion" element={<Navigate to="/onboarding/albums" replace />} />
              <Route path="help" element={<Navigate to="/onboarding/albums" replace />} />
              <Route path="congrats" element={<OnboardingCongrats />} />
            </Route>
            <Route
              path="/claim"
              element={<CollectorClaim />}
            />
            <Route
              path="/link-confirm"
              element={<CollectorLinkConfirm />}
            />
            <Route
              path="*"
              element={
                <div className="container-gm py-24 text-center">
                  <div className="overline overline-gold">404</div>
                  <h1 className="font-serif text-4xl md:text-6xl mt-4">Page not found.</h1>
                </div>
              }
            />
          </Route>
        </Routes>
        </BrowserRouter>
      </CollectorAuthProvider>
    </AuthProvider>
  );
}
